import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronUpDownIcon,
  CheckIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { policyApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge, DataTable, StatCard } from '../components/ui';
import type { Column, BadgeVariant } from '../components/ui';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  DRAFT: 'draft',
  UNDER_REVIEW: 'info',
  ARCHIVED: 'neutral',
  EXPIRED: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  UNDER_REVIEW: 'Under Review',
  ARCHIVED: 'Archived',
  EXPIRED: 'Expired',
};

const TABS = ['All', 'Active', 'Under Review', 'Draft', 'Archived', 'Overdue'] as const;

const TAB_STATUS_MAP: Record<string, string | undefined> = {
  All: undefined,
  Active: 'ACTIVE',
  'Under Review': 'UNDER_REVIEW',
  Draft: 'DRAFT',
  Archived: 'ARCHIVED',
  Overdue: 'OVERDUE',
};

const POLICY_CATEGORIES = [
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'LENDING', label: 'Lending' },
  { value: 'BSA_AML', label: 'BSA/AML' },
  { value: 'INFORMATION_SECURITY', label: 'Information Security' },
  { value: 'PRIVACY', label: 'Privacy' },
  { value: 'FAIR_LENDING', label: 'Fair Lending' },
  { value: 'VENDOR_MANAGEMENT', label: 'Vendor Management' },
  { value: 'DEPOSITS', label: 'Deposits' },
  { value: 'HR', label: 'Human Resources' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'OTHER', label: 'Other' },
];

const DEFAULT_FORM = {
  title: '',
  description: '',
  category: POLICY_CATEGORIES[0].value,
  owner: '',
  department: '',
  effectiveDate: '',
  reviewFrequencyDays: 365,
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function PolicyManagementPage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const limit = 20;

  const activeStatus = TAB_STATUS_MAP[TABS[selectedTab]];

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: dashboardData } = useQuery({
    queryKey: ['policies', 'dashboard', teamId],
    queryFn: () => policyApi.dashboard(teamId!),
    enabled: !!teamId,
  });
  const dashboard = dashboardData?.data;

  const { data: policiesData, isLoading } = useQuery({
    queryKey: ['policies', teamId, activeStatus, page, limit],
    queryFn: () =>
      policyApi.list(teamId!, {
        status: activeStatus,
        page,
        limit,
      }),
    enabled: !!teamId,
  });
  const policies = policiesData?.data?.policies || [];

  // ── Mutations ────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => policyApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy created successfully');
      setShowCreateModal(false);
      setFormData(DEFAULT_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create policy');
    },
  });

  const handleCreate = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    createMutation.mutate({
      ...formData,
      reviewFrequencyDays: Number(formData.reviewFrequencyDays) || 365,
    });
  };

  // ── Table columns ────────────────────────────────────────────────────────

  const columns: Column<any>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      sortValue: (row: any) => row.title,
      render: (row: any) => (
        <Link
          to={`/policies/${row.id}`}
          className="font-medium text-teal-600 hover:text-teal-700 hover:underline"
        >
          {row.title}
        </Link>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row: any) => (
        <StatusBadge variant="info">
          {POLICY_CATEGORIES.find((c) => c.value === row.category)?.label || row.category}
        </StatusBadge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: any) => (
        <StatusBadge variant={STATUS_VARIANT[row.status] || 'neutral'}>
          {STATUS_LABEL[row.status] || row.status}
        </StatusBadge>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (row: any) => <span className="text-gray-700">{row.owner || '—'}</span>,
    },
    {
      key: 'lastReviewed',
      header: 'Last Reviewed',
      sortable: true,
      sortValue: (row: any) => row.lastReviewedAt ? new Date(row.lastReviewedAt).getTime() : 0,
      render: (row: any) => (
        <span className="text-gray-500">
          {row.lastReviewedAt ? new Date(row.lastReviewedAt).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'nextReview',
      header: 'Next Review',
      sortable: true,
      sortValue: (row: any) => row.nextReviewDate ? new Date(row.nextReviewDate).getTime() : 0,
      render: (row: any) => (
        <span className="text-gray-500">
          {row.nextReviewDate ? new Date(row.nextReviewDate).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row: any) => (
        <Link
          to={`/policies/${row.id}`}
          className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 transition-colors"
        >
          <EyeIcon className="h-3.5 w-3.5" />
          View
        </Link>
      ),
    },
  ];

  // ── Selected category for Listbox ────────────────────────────────────────

  const selectedCategory = POLICY_CATEGORIES.find((c) => c.value === formData.category) || POLICY_CATEGORIES[0];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="policy_management">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Policies & Procedures"
          description="Manage your institution's compliance policies, track review schedules, and ensure regulatory alignment."
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/policies/bulk-import')}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                  title="Bulk-import historical policies from another vendor"
                >
                  Bulk import
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  New Policy
                </button>
              </div>
            </PermissionGate>
          }
        />

        {/* Stats row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={DocumentTextIcon}
            label="Total Policies"
            value={dashboard?.totalPolicies ?? 0}
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Active"
            value={dashboard?.activePolicies ?? 0}
          />
          <StatCard
            icon={ClockIcon}
            label="Due for Review"
            value={dashboard?.dueForReview ?? 0}
          />
          <StatCard
            icon={ExclamationTriangleIcon}
            label="Overdue"
            value={dashboard?.overdue ?? 0}
          />
        </div>

        {/* Tab bar */}
        <Tab.Group selectedIndex={selectedTab} onChange={(index) => { setSelectedTab(index); setPage(1); }}>
          <Tab.List className="flex space-x-1 border-b border-gray-200">
            {TABS.map((tab) => (
              <Tab
                key={tab}
                className={({ selected }) =>
                  clsx(
                    'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                    selected
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  )
                }
              >
                {tab}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            {TABS.map((tab) => (
              <Tab.Panel key={tab}>
                <DataTable
                  columns={columns}
                  data={policies}
                  isLoading={isLoading}
                  keyExtractor={(row: any) => row.id}
                  emptyTitle="No policies found"
                  emptyDescription="Create your first policy to get started with policy management."
                  emptyAction={
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
                    >
                      <PlusIcon className="h-4 w-4" />
                      New Policy
                    </button>
                  }
                  pageSize={limit}
                />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>

        {/* ── Create Policy Modal ─────────────────────────────────────────── */}
        <Transition appear show={showCreateModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      New Policy
                    </Dialog.Title>

                    <div className="mt-4 space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="e.g., Anti-Money Laundering Policy"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          placeholder="Brief description of the policy purpose and scope"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Category (Listbox) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <Listbox
                          value={formData.category}
                          onChange={(value) => setFormData({ ...formData, category: value })}
                        >
                          <div className="relative mt-1">
                            <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                              <span className="block truncate">{selectedCategory.label}</span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {POLICY_CATEGORIES.map((cat) => (
                                  <Listbox.Option
                                    key={cat.value}
                                    value={cat.value}
                                    className={({ active }) =>
                                      clsx(
                                        'relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                        active ? 'bg-teal-50 text-teal-700' : 'text-gray-900',
                                      )
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span className={clsx('block truncate', selected && 'font-medium')}>
                                          {cat.label}
                                        </span>
                                        {selected && (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Owner + Department */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Owner</label>
                          <input
                            type="text"
                            value={formData.owner}
                            onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                            placeholder="User ID or name"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Department</label>
                          <input
                            type="text"
                            value={formData.department}
                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            placeholder="e.g., Compliance"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>

                      {/* Effective Date + Review Frequency */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Effective Date</label>
                          <input
                            type="date"
                            value={formData.effectiveDate}
                            onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Review Frequency (days)
                          </label>
                          <input
                            type="number"
                            value={formData.reviewFrequencyDays}
                            onChange={(e) =>
                              setFormData({ ...formData, reviewFrequencyDays: Number(e.target.value) || 365 })
                            }
                            min={1}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreate}
                        disabled={!formData.title.trim() || createMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                      >
                        {createMutation.isPending ? 'Creating...' : 'Create Policy'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
